import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { ASSETS, BRAND, NAV_LINKS } from "@/lib/brand";

/* Dropdown trigger that opens on hover (desktop) — parent label is NOT a link */
function NavDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const { pathname } = useLocation();
  const isActive = item.children.some((c) => pathname.startsWith(c.to));

  const openNow = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 140);
  };

  return (
    <div
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onFocus={openNow}
      onBlur={closeSoon}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-link-${item.key}`}
        className={`flex items-center gap-1 whitespace-nowrap text-[0.7rem] tracking-[0.18em] uppercase font-medium transition-colors ${
          isActive ? "text-bronze" : "text-ink_soft hover:text-bronze"
        }`}
      >
        {item.label}
        <ChevronDown
          size={12}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[18rem] z-50 transition-all duration-200 origin-top ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
        data-testid={`nav-dropdown-${item.key}`}
      >
        <div className="bg-cream border border-bronze/20 shadow-xl p-2">
          {item.children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-dropdown-item-${c.to.replace(/\//g, "-")}`}
              className="block px-4 py-3 hover:bg-beige transition-colors group"
            >
              <div className="font-serif text-lg text-ink group-hover:text-bronze">
                {c.label}
              </div>
              {c.desc && (
                <div className="text-xs text-olive mt-0.5">{c.desc}</div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState({});

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const toggleMobileGroup = (key) =>
    setMobileExpanded((p) => ({ ...p, [key]: !p[key] }));

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream/95 backdrop-blur-xl border-b border-bronze/15"
          : "bg-cream/40 backdrop-blur-sm"
      }`}
    >
      <div className="container-page flex items-center justify-between py-3 md:py-4">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-2"
          onClick={() => setOpen(false)}
        >
          <img
            src={ASSETS.logoBlack}
            alt="A Homely Abode logo"
            className="h-14 md:h-16 w-auto object-contain"
          />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-lg md:text-xl text-ink tracking-tight whitespace-nowrap">
              {BRAND.name}
            </span>
            <span className="text-[0.55rem] md:text-[0.6rem] tracking-[0.28em] uppercase text-olive whitespace-nowrap mt-0.5">
              By {BRAND.founder}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {NAV_LINKS.map((l) =>
            l.children ? (
              <NavDropdown key={l.key} item={l} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[0.7rem] tracking-[0.18em] uppercase font-medium transition-colors ${
                    isActive ? "text-bronze" : "text-ink_soft hover:text-bronze"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp"
            className="hidden md:inline-flex btn-primary whitespace-nowrap"
          >
            Start Journey
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center border border-bronze/40 text-ink"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden fixed inset-x-0 top-[80px] bg-cream border-b border-bronze/20 transition-all duration-500 overflow-y-auto ${
          open ? "max-h-[85vh] py-6" : "max-h-0 py-0"
        }`}
        data-testid="nav-mobile-panel"
      >
        <div className="container-page flex flex-col gap-2">
          {NAV_LINKS.map((l) => {
            if (!l.children) {
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `font-serif text-3xl py-2 ${isActive ? "text-bronze" : "text-ink"}`
                  }
                >
                  {l.label}
                </NavLink>
              );
            }
            const expanded = !!mobileExpanded[l.key];
            return (
              <div key={l.key} className="border-b border-bronze/10 pb-2">
                <button
                  onClick={() => toggleMobileGroup(l.key)}
                  data-testid={`nav-mobile-toggle-${l.key}`}
                  aria-label={`Expand ${l.label}`}
                  className="w-full flex items-center justify-between py-2"
                >
                  <span className={`font-serif text-3xl ${expanded ? "text-bronze" : "text-ink"}`}>
                    {l.label}
                  </span>
                  <span className="h-9 w-9 inline-flex items-center justify-center border border-bronze/30 text-bronze">
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    expanded ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <div className="pl-2 pb-3 flex flex-col gap-2">
                    {l.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        data-testid={`nav-mobile-sublink-${c.to.replace(/\//g, "-")}`}
                        className={({ isActive }) =>
                          `text-base tracking-wide py-1.5 ${
                            isActive ? "text-bronze" : "text-ink_soft"
                          }`
                        }
                      >
                        — {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-mobile-cta-whatsapp"
            className="btn-primary mt-4 self-start"
          >
            WhatsApp Kiap Yong
          </a>
        </div>
      </div>
    </header>
  );
}
